// İmza tuvali.
//
// Çizgiler 0..1 aralığında normalize edilmiş noktalar olarak tutulur, bitmap
// olarak değil. Sebebi: imza alanı küçük kutudan tam ekrana açılıyor ve cihaz
// döndürülebiliyor. Bitmap tutulursa yeniden boyutlandırmada imza deforme olur
// — Adobe'nin mobil imza yakalama dokümanının da uyardığı sorun. Nokta modeli
// her boyutta temiz çizilir ve PDF'e sabit yüksek çözünürlükte gömülür.

type Pt = { x: number; y: number };

export class SignPad {
  private strokes: Pt[][] = [];
  private canvas: HTMLCanvasElement | null = null;
  private ctx: CanvasRenderingContext2D | null = null;
  private drawing = false;
  private hintRaf = 0;
  private hintOn = false;
  private onFirstInk?: () => void;

  get isEmpty(): boolean { return this.strokes.length === 0; }

  /** Tuvali bağlar. Önceki çizgiler korunur, yeni boyuta göre yeniden çizilir. */
  attach(canvas: HTMLCanvasElement, onFirstInk?: () => void) {
    this.detach();
    this.canvas = canvas;
    this.onFirstInk = onFirstInk;
    this.ctx = canvas.getContext('2d');
    this.fit();
    canvas.addEventListener('pointerdown', this.down);
    canvas.addEventListener('pointermove', this.move);
    canvas.addEventListener('pointerup', this.up);
    canvas.addEventListener('pointercancel', this.up);
    canvas.addEventListener('pointerleave', this.up);
    window.addEventListener('resize', this.fit);
  }

  detach() {
    this.stopHint();
    const c = this.canvas;
    if (!c) return;
    c.removeEventListener('pointerdown', this.down);
    c.removeEventListener('pointermove', this.move);
    c.removeEventListener('pointerup', this.up);
    c.removeEventListener('pointercancel', this.up);
    c.removeEventListener('pointerleave', this.up);
    window.removeEventListener('resize', this.fit);
    this.canvas = null;
    this.ctx = null;
  }

  clear() {
    this.strokes = [];
    this.redraw();
  }

  private fit = () => {
    const c = this.canvas;
    if (!c || !this.ctx) return;
    const r = c.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    c.width = Math.max(1, Math.floor(r.width * dpr));
    c.height = Math.max(1, Math.floor(r.height * dpr));
    this.redraw();
  };

  private pos(e: PointerEvent): Pt {
    const r = this.canvas!.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  }

  private down = (e: PointerEvent) => {
    e.preventDefault();
    this.stopHint();
    const first = this.isEmpty;
    this.drawing = true;
    this.canvas!.setPointerCapture(e.pointerId);
    this.strokes.push([this.pos(e)]);
    if (first && this.onFirstInk) this.onFirstInk();
  };

  private move = (e: PointerEvent) => {
    if (!this.drawing) return;
    e.preventDefault();
    this.strokes[this.strokes.length - 1].push(this.pos(e));
    this.redraw();
  };

  private up = () => { this.drawing = false; };

  private strokePath(ctx: CanvasRenderingContext2D, pts: Pt[], w: number, h: number, lw: number) {
    if (pts.length === 0) return;
    ctx.beginPath();
    ctx.moveTo(pts[0].x * w, pts[0].y * h);
    if (pts.length === 1) {
      // Tek dokunuş da görünür bir nokta bıraksın
      ctx.lineTo(pts[0].x * w + 0.6, pts[0].y * h);
    } else {
      // İki nokta arasını orta noktalardan geçen eğriyle yumuşat
      for (let i = 1; i < pts.length - 1; i++) {
        const mx = (pts[i].x + pts[i + 1].x) / 2 * w;
        const my = (pts[i].y + pts[i + 1].y) / 2 * h;
        ctx.quadraticCurveTo(pts[i].x * w, pts[i].y * h, mx, my);
      }
      const last = pts[pts.length - 1];
      ctx.lineTo(last.x * w, last.y * h);
    }
    ctx.lineWidth = lw;
    ctx.stroke();
  }

  private redraw() {
    const c = this.canvas, ctx = this.ctx;
    if (!c || !ctx) return;
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1f212a';
    const lw = Math.max(2, c.width * 0.004);
    this.strokes.forEach(s => this.strokePath(ctx, s, c.width, c.height, lw));
  }

  /**
   * Boş tuvalde örnek bir imzayı yavaşça çizer: kullanıcı nereye ve nasıl
   * imza atacağını görür. İlk dokunuşta kalıcı olarak durur.
   * Azaltılmış hareket tercihi açıksa hiç çalışmaz.
   */
  startHint() {
    if (!this.canvas || !this.ctx || !this.isEmpty) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.hintOn = true;
    const c = this.canvas, ctx = this.ctx;
    // Elle yazılmış bir ada benzeyen örnek yol (normalize)
    const path: Pt[] = [];
    for (let t = 0; t <= 1.0001; t += 0.004) {
      const x = 0.16 + t * 0.62;
      const y = 0.62
        - Math.sin(t * Math.PI * 2.6) * 0.17
        - Math.sin(t * Math.PI * 7.4) * 0.045
        - t * 0.05;
      path.push({ x, y });
    }
    let i = 0;
    const step = () => {
      if (!this.hintOn || !this.isEmpty) return;
      ctx.clearRect(0, 0, c.width, c.height);
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
      ctx.strokeStyle = 'rgba(21, 124, 128, 0.38)';
      this.strokePath(ctx, path.slice(0, i), c.width, c.height, Math.max(2, c.width * 0.004));
      i += 6;
      if (i > path.length + 90) i = 0;           // kısa bir bekleme sonrası baştan
      this.hintRaf = requestAnimationFrame(step);
    };
    this.hintRaf = requestAnimationFrame(step);
  }

  stopHint() {
    this.hintOn = false;
    if (this.hintRaf) cancelAnimationFrame(this.hintRaf);
    this.hintRaf = 0;
    if (this.isEmpty && this.ctx && this.canvas) {
      this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    }
  }

  /** PDF'e gömmek için sabit çözünürlüklü PNG. Pad boyutundan bağımsız. */
  toPNG(width = 900, height = 300): string {
    const off = document.createElement('canvas');
    off.width = width; off.height = height;
    const ctx = off.getContext('2d')!;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = '#1f212a';
    this.strokes.forEach(s => this.strokePath(ctx, s, width, height, Math.max(3, width * 0.005)));
    return off.toDataURL('image/png');
  }
}
